import {wait} from './setTimeout'
import {Attempt, asyncAttempt} from './function'
import type {CheckIfShouldRetryFunction} from './dsi/retries'
import _ from 'lodash'

const DEFAULT_RETRIES = 3
const DEFAULT_WAIT = 0

export const taskWithRetries = async <T, E extends Error>(
    task: () => Promise<T>,
    shouldRetry: (error: E) => boolean = () => true,
    retries: number = DEFAULT_RETRIES,
    waitInterval: number = DEFAULT_WAIT
): Promise<T> => {
    try {
        return await task()
    } catch (error) {
        if (retries <= 0 || !shouldRetry(error as E)) {
            throw error
        }

        await wait(waitInterval)

        return await taskWithRetries(task, shouldRetry, retries - 1, waitInterval)
    }
}

const extractErrorFromResult = <T, E extends Error>(result: Attempt<Awaited<T>, E>) => {
    if (result.didThrow) {
        return result.error
    }
    if (_.get(result, ['value', 'success']) === false) {
        return result.value
    }
}

const shouldRetry = <T, E extends Error>(
    attemptResult: Attempt<Awaited<T>, E>,
    checkIfShouldRetry: CheckIfShouldRetryFunction<T, E>
) => {
    const error = extractErrorFromResult(attemptResult)
    if (!error) {
        return {shouldRetry: false}
    }
    return checkIfShouldRetry(error)
}

export const taskWithRetriesAndInfo = async <T, E extends Error>(
    task: () => Promise<T>,
    checkIfShouldRetry: CheckIfShouldRetryFunction<T, E> = () => ({shouldRetry: true}),
    retries: number = DEFAULT_RETRIES,
    waitInterval: number = DEFAULT_WAIT
): Promise<{result: Attempt<T, E>; retries: number; reason?: string}> => {
    let result = await asyncAttempt<typeof task, E>(task)
    let should = shouldRetry(result, checkIfShouldRetry)
    let attempts = 0
    let reason

    while (attempts < retries && should.shouldRetry) {
        reason = should.reason
        await wait(waitInterval)
        result = await asyncAttempt<typeof task, E>(task)
        should = shouldRetry(result, checkIfShouldRetry)
        attempts++
    }

    return {result, retries: attempts, reason}
}
